// eslint-disable-next-line no-restricted-imports -- Direct HTTP calls needed for Redux session hydration outside React
import request from "superagent";

import { isNil } from "@clipboard-health/util-ts";

import { apiClient } from "../../api/apiClient";
import { getAuthHeader } from "../../../utils/superagent";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import {
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logWorkerAccountLoginFriction,
} from "../loginRum";
import { SSO_CALLBACK_PATH } from "../RipplingSso/paths";
import { cognitoSignOut, hasStoredCognitoSession } from "./cognitoAuth";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";
import { isWorkerToken } from "./isWorkerToken";
import { isEmployeeToken } from "./isEmployeeToken";
import { NO_ASSOCIATED_WORKPLACES_ERROR_MESSAGE } from "./sessionInitErrors";
import {
  ADMIN_PROFILE_SUCCESS,
  LOGIN_REQUESTED,
  LOGIN_SUCCESS,
  LOGOUT,
  SESSION_LOGIN_SUCCESS,
  SESSION_TOKEN_READY,
} from "./sessionActionTypes";

const HCF_USER_EVENTS = {
  LOGGED_IN: "HCF Logged In",
} as const;

const REQUEST_ENTITY_TOO_LARGE_STATUS = 413;
const REQUEST_ENTITY_TOO_LARGE_PAYLOAD_SOURCE = "cognito_id_token_authorization_header";

/**
 * Outcome of a session-init attempt. Callers treat anything other than
 * `HYDRATED` as "not signed in"; the login UI singles out `WORKER_ACCOUNT` to
 * show the friendly wrong-app message instead of the generic retry copy.
 */
export const SESSION_INIT_OUTCOME = {
  HYDRATED: "hydrated",
  NOT_HYDRATED: "not_hydrated",
  WORKER_ACCOUNT: "worker_account",
} as const;

export type SessionInitOutcome = (typeof SESSION_INIT_OUTCOME)[keyof typeof SESSION_INIT_OUTCOME];

export interface SessionInitResult {
  outcome: SessionInitOutcome;
  error?: unknown;
}

const consumeOverrideUserIdUrlParameter = (): string | null => {
  const searchParameters = new URLSearchParams(window.location.search);
  const userId = searchParameters.get("overrideUserId");
  if (userId) {
    searchParameters.delete("overrideUserId");
    history.replaceState(
      window.history.state,
      "",
      `${window.location.origin}${window.location.pathname}${searchParameters.toString() ? `?${searchParameters.toString()}` : ""}`
    );
    return userId;
  }

  const requestedUrlParameter = searchParameters.get("requestedUrl");
  if (requestedUrlParameter) {
    try {
      const requestedUrl = new URL(requestedUrlParameter, window.location.origin);
      const nestedParameters = new URLSearchParams(requestedUrl.search);
      const nestedUserId = nestedParameters.get("overrideUserId");

      if (nestedUserId) {
        return nestedUserId;
      }
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Fetches the user record and dispatches LOGIN_SUCCESS.
 *
 * Focused replacement for the legacy `get()` thunk from
 * `src/modules/session.ts`, without the moment-timezone dependency chain.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchSession(dispatch: any, userId: string) {
  localStorage.setItem("selectedUser", userId);
  dispatch({ type: LOGIN_REQUESTED });

  try {
    const response = await request
      .get(`${(globalThis as Record<string, unknown>).api}/user/get/${userId}`)
      .set(await getAuthHeader());

    dispatch({
      type: LOGIN_SUCCESS,
      data: { ...response.body },
    });
  } catch (error: unknown) {
    showErrorToast(error instanceof Error ? error.message : "Failed to fetch user session");
  }
}

interface FacilityUserRecord {
  _id: string;
  name: string;
  email: string;
  phone: string;
  tmz: string;
  roles: string[];
  permissions: string[];
  notify: unknown;
  isEmailVerified: boolean;
  isSuspicious: boolean;
  intercomHash?: string;
  facilities: Array<{ userId: string }>;
}

// Uses `apiClient` (not superagent) so a transiently rejected ID token
// can be force-refreshed and replayed by the response interceptor instead
// of cascading into a session-init LOGOUT.
async function getFacilityUser(): Promise<{ user: FacilityUserRecord }> {
  const response = await apiClient.get<{ user: FacilityUserRecord }>("/facilityUser/findByEmail");
  return response.data;
}

/**
 * Initializes the user session from Cognito tokens.
 *
 * Checks for an existing valid Cognito session via Amplify and hydrates
 * the Redux session.
 *
 * Returns `true` if a valid Cognito session was found and restored,
 * `false` if no Cognito session exists.
 */
// Deduplicates concurrent calls to initCognitoSession. When multiple callers
// (e.g. SSO callback + AppLayout's useEffect) dispatch initCognitoSession at
// the same time, only the first call runs the actual session hydration. All
// subsequent calls return the same in-flight promise. This prevents the
// SESSION_TOKEN_READY: { ready: false } dispatch from firing multiple times,
// which would cause render loops and duplicate API calls.
let inFlightSessionInit: Promise<SessionInitResult> | undefined;

/**
 * Resolves the analytics event name and `source` tag for a session-init
 * failure, based on where the session is being hydrated (SSO callback vs
 * passkey vs password login).
 */
function resolveSessionFailure(options?: { authMethod?: "passkey" }): {
  eventName: (typeof APP_V2_APP_EVENTS)[keyof typeof APP_V2_APP_EVENTS];
  source: string;
} {
  const isSsoCallback = globalThis.location?.pathname === SSO_CALLBACK_PATH;
  const isPasskey = options?.authMethod === "passkey";

  if (isSsoCallback) {
    return {
      eventName: APP_V2_APP_EVENTS.RIPPLING_SSO_SESSION_HYDRATION_FAILURE,
      source: "sso-callback",
    };
  }
  if (isPasskey) {
    return {
      eventName: APP_V2_APP_EVENTS.PASSKEY_LOGIN_COGNITO_SESSION_FAILURE,
      source: "passkey-session-init",
    };
  }
  return {
    eventName: APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE,
    source: "cognito-session-init",
  };
}

function getObjectRecord(value: unknown): Record<string, unknown> | undefined {
  if (isNil(value) || typeof value !== "object") {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function getHttpStatus(error: unknown): number | undefined {
  const errorRecord = getObjectRecord(error);
  if (isNil(errorRecord)) {
    return undefined;
  }

  if (typeof errorRecord.status === "number") {
    return errorRecord.status;
  }
  if (typeof errorRecord.statusCode === "number") {
    return errorRecord.statusCode;
  }

  const responseRecord = getObjectRecord(errorRecord.response);
  if (isNil(responseRecord)) {
    return undefined;
  }

  if (typeof responseRecord.status === "number") {
    return responseRecord.status;
  }
  if (typeof responseRecord.statusCode === "number") {
    return responseRecord.statusCode;
  }

  return undefined;
}

function isRequestEntityTooLargeError(error: unknown): boolean {
  return getHttpStatus(error) === REQUEST_ENTITY_TOO_LARGE_STATUS;
}

// `AxiosError: Network Error` is identical for DNS / CORS / TLS / timeout; the
// axios `code` plus `config` + the underlying browser `cause` (fetch adapter)
// are what distinguish them. Excludes headers/body to avoid leaking tokens or
// PII; excludes `request` because it's the live XHR (doesn't serialize).
function extractErrorDetails(error: unknown): Record<string, string | number> {
  if (!error || typeof error !== "object") {
    return {};
  }
  const record = error as Record<string, unknown>;
  const details: Record<string, string | number> = {};

  if (typeof record.name === "string") {
    details.errorName = record.name;
  }
  if (typeof record.code === "string") {
    details.errorCode = record.code;
  }

  const config = record.config as
    | { url?: unknown; method?: unknown; baseURL?: unknown }
    | undefined;
  if (typeof config?.url === "string") {
    details.errorUrl = config.url;
  }
  if (typeof config?.method === "string") {
    details.errorMethod = config.method;
  }
  if (typeof config?.baseURL === "string") {
    details.errorBaseUrl = config.baseURL;
  }

  if (typeof record.status === "number") {
    details.errorStatus = record.status;
  } else {
    const response = record.response as { status?: unknown } | undefined;
    if (typeof response?.status === "number") {
      details.errorStatus = response.status;
    }
  }

  const cause = record.cause as { name?: unknown; message?: unknown } | undefined;
  if (typeof cause?.name === "string") {
    details.errorCauseName = cause.name;
  }
  if (typeof cause?.message === "string") {
    details.errorCauseMessage = cause.message;
  }

  return details;
}

interface SessionFailureMetadata extends Record<string, string | number | undefined> {
  source: string;
  outcome: (typeof LOGIN_OUTCOME)[keyof typeof LOGIN_OUTCOME];
  reason: string;
  payloadSource?: string;
}

function buildSessionFailureMetadata(error: unknown, source: string): SessionFailureMetadata {
  if (isRequestEntityTooLargeError(error)) {
    return {
      source,
      outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
      reason: LOGIN_REASON.REQUEST_ENTITY_TOO_LARGE,
      payloadSource: REQUEST_ENTITY_TOO_LARGE_PAYLOAD_SOURCE,
      ...extractErrorDetails(error),
    };
  }

  return {
    source,
    outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
    reason: isNetworkFailure(error)
      ? LOGIN_REASON.NETWORK_FAILURE
      : LOGIN_REASON.SESSION_HYDRATION_FAILED,
    ...extractErrorDetails(error),
  };
}

function recordSessionFailure(params: {
  eventName: (typeof APP_V2_APP_EVENTS)[keyof typeof APP_V2_APP_EVENTS];
  error: unknown;
  metadata: ReturnType<typeof buildSessionFailureMetadata>;
}): void {
  const { eventName, error, metadata } = params;

  if (metadata.reason === LOGIN_REASON.REQUEST_ENTITY_TOO_LARGE) {
    logEvent(eventName, metadata);
    return;
  }

  logError(eventName, { error, metadata });
}

type AuthSessionResult = Awaited<ReturnType<typeof fetchAuthSessionWithLock>>;
type SessionIdToken = NonNullable<NonNullable<AuthSessionResult["tokens"]>["idToken"]>;

/**
 * Fetches the Cognito session for init and, when the first read yields no ID
 * token, retries once with a forced refresh — but only while a stored Cognito
 * session still exists.
 *
 * Mirrors the request interceptor (`getCognitoTokenForRequest`), which recovers
 * the large majority of first-attempt token failures with the same
 * force-refresh retry. The first `fetchAuthSession` after a
 * `window.location.reload()` transiently returns no token far more often on
 * mobile WebKit (Safari on iOS/iPadOS, and every iOS browser, which are all
 * WebKit) than on desktop; without this retry, the admin workplace-switch
 * reload mistook that transient gap for a signed-out user and logged the admin
 * out.
 *
 * Never throws. A thrown first attempt is swallowed and retried; a thrown retry
 * resolves to "no token". The caller decides sign-out vs. keep-session from
 * whether a stored session still exists — a definitive auth rejection
 * (RefreshTokenReuse / NotAuthorized / invalid_grant) makes Amplify clear the
 * stored tokens, so `hasStoredCognitoSession()` flips to false there.
 */
async function resolveSessionIdToken(): Promise<SessionIdToken | undefined> {
  try {
    const session = await fetchAuthSessionWithLock();
    const idToken = session.tokens?.idToken;
    if (idToken) {
      return idToken;
    }
  } catch {
    // Swallow: a genuinely signed-out user is detected below by the absence of
    // a stored session; every other first-attempt failure gets the retry.
  }

  if (!hasStoredCognitoSession()) {
    return undefined;
  }

  try {
    const session = await fetchAuthSessionWithLock({ forceRefresh: true });
    return session.tokens?.idToken;
  } catch {
    return undefined;
  }
}

/**
 * Marks the session token ready and signs the user out ONLY when no stored
 * Cognito session remains. When a stored session is still present the failure
 * is transient (e.g. the mobile-WebKit token gap on the post-switch reload), so
 * the persisted Redux session is kept and the request interceptor refreshes the
 * token on the next API call. Returns `NOT_HYDRATED` (session not freshly
 * hydrated).
 */
function finalizeWithoutHydration(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: any,
  params: { source: string; reason: string; error?: unknown }
): SessionInitResult {
  if (hasStoredCognitoSession()) {
    logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_INIT_TRANSIENT_FAILURE, {
      source: params.source,
      reason: params.reason,
    });
  } else {
    dispatch({ type: LOGOUT });
  }
  dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
  return { outcome: SESSION_INIT_OUTCOME.NOT_HYDRATED, error: params.error };
}

export const initCognitoSession =
  (options?: { authMethod?: "passkey" }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (dispatch: any): Promise<SessionInitResult> => {
    if (inFlightSessionInit) {
      return inFlightSessionInit;
    }

    inFlightSessionInit = (async () => {
      dispatch({
        type: SESSION_TOKEN_READY,
        data: { ready: false },
      });

      const { eventName, source } = resolveSessionFailure(options);

      try {
        const idToken = await resolveSessionIdToken();
        const email = idToken?.payload?.email;

        if (!idToken) {
          // No usable token. Only sign out when the stored Cognito session is
          // genuinely gone; otherwise keep the persisted session — clearing it
          // here is what bounced still-authenticated admins to /login after the
          // workplace-switch reload on mobile WebKit (Safari iOS/iPadOS).
          return finalizeWithoutHydration(dispatch, { source, reason: "empty_token" });
        }

        // Route based on `custom:user_types` from the Cognito ID token,
        // not on email pattern. _3p vendor-bypass accounts are
        // EMPLOYEE-typed and must hit handleEmployeeLogin; the
        // aliased-employee-as-facility testing convention is preserved
        // inside isEmployeeToken.
        const userTypesAttribute = idToken.payload["custom:user_types"];

        // A worker-only token would otherwise fall into workplace hydration and
        // 403 on `/facilityUser/findByEmail`, surfacing as the misleading
        // "session not restored" loop (ET-1285). Short-circuit before any
        // hydration. Runs before the email guard because it depends only on
        // `custom:user_types`.
        if (isWorkerToken(userTypesAttribute)) {
          logWorkerAccountLoginFriction({ source });
          try {
            await cognitoSignOut();
          } catch {
            // A failed sign-out must not break the friendly-message path.
          }
          dispatch({ type: LOGOUT });
          dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
          return { outcome: SESSION_INIT_OUTCOME.WORKER_ACCOUNT };
        }

        if (typeof email !== "string") {
          return finalizeWithoutHydration(dispatch, { source, reason: "empty_token" });
        }

        dispatch({
          type: SESSION_TOKEN_READY,
          data: { ready: true },
        });

        await (isEmployeeToken(email, userTypesAttribute)
          ? handleEmployeeLogin(dispatch)
          : handleFacilityLogin(dispatch));

        return { outcome: SESSION_INIT_OUTCOME.HYDRATED };
      } catch (error: unknown) {
        // Hydration threw after a valid token (network/5xx, or a definitive
        // auth rejection that already cleared the stored session). Record it,
        // then sign out only when no stored session remains — a transient
        // hydration failure for a user whose Cognito session is intact must not
        // log them out.
        recordSessionFailure({
          eventName,
          error,
          metadata: buildSessionFailureMetadata(error, source),
        });
        if (!hasStoredCognitoSession()) {
          dispatch({ type: LOGOUT });
        }
        dispatch({ type: SESSION_TOKEN_READY, data: { ready: true } });
        return { outcome: SESSION_INIT_OUTCOME.NOT_HYDRATED, error };
      } finally {
        inFlightSessionInit = undefined;
      }
    })();

    return inFlightSessionInit;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleEmployeeLogin(dispatch: any): Promise<void> {
  const response = await request
    .get(`${(globalThis as Record<string, unknown>).api}/user/getByEmail`)
    .set(await getAuthHeader());

  dispatch({ type: ADMIN_PROFILE_SUCCESS, data: { user: response.body } });

  const user = { ...response.body };

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedUser", overrideUserId);
  }

  const selectedUser = localStorage.getItem("selectedUser");

  // SESSION_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by SESSION_LOGIN_SUCCESS.
  dispatch({
    type: SESSION_LOGIN_SUCCESS,
    data: {
      user: {
        access: user.roles,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, selectedUser || user._id);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleFacilityLogin(dispatch: any): Promise<void> {
  const { user } = await getFacilityUser();
  if (!user?.facilities?.length) {
    throw new Error(NO_ASSOCIATED_WORKPLACES_ERROR_MESSAGE);
  }
  const facilityIdsArray = user.facilities.map((facility) => facility.userId);

  const overrideUserId = consumeOverrideUserIdUrlParameter();
  if (overrideUserId) {
    localStorage.setItem("selectedFacility", overrideUserId);
  }

  const selectedFacility = localStorage.getItem("selectedFacility");
  const selectedUser = localStorage.getItem("selectedUser");
  const isSelectedFacilityExists =
    selectedFacility !== null && facilityIdsArray.includes(selectedFacility);
  const userId: string = isSelectedFacilityExists
    ? selectedFacility
    : (facilityIdsArray[0] ?? selectedUser ?? "");
  if (!userId) {
    throw new Error("Unable to determine session user id for facility user");
  }

  if (!isSelectedFacilityExists) {
    localStorage.setItem("selectedFacility", facilityIdsArray[0]);
    logEvent(HCF_USER_EVENTS.LOGGED_IN, {
      workplaceId: userId,
      workplaceUserId: user?._id,
    });
  }

  // SESSION_LOGIN_SUCCESS must be dispatched BEFORE fetchSession because the
  // LOGIN_SUCCESS reducer (in session.ts) reads state.user._id for analytics.
  // state.user is set by SESSION_LOGIN_SUCCESS.
  dispatch({
    type: SESSION_LOGIN_SUCCESS,
    data: {
      user: {
        facilityIds: facilityIdsArray,
        tmz: user.tmz,
        access: user.roles,
        permissions: user.permissions,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        notify: user.notify,
        isEmailVerified: user.isEmailVerified,
        isSuspicious: user.isSuspicious,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });

  await fetchSession(dispatch, userId);
}
